import { Link, useRouterState } from "@tanstack/react-router";
import {
  Home,
  Users,
  Wallet,
  Heart,
  LayoutGrid,
  Sparkles,
  Sun,
  Moon,
  PanelLeftClose,
  PanelLeftOpen,
} from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import dryvnLogo from "../assets/dryvn-life-logo.png.asset.json";
import { useMe } from "@/lib/queries";

const nav = [
  { to: "/", label: "Today", icon: Home },
  { to: "/dryvn", label: "Dryvn", icon: Sparkles },
  { to: "/family", label: "Family", icon: Users },
  { to: "/money", label: "Money", icon: Wallet },
  { to: "/health", label: "Health", icon: Heart },
  { to: "/more", label: "More", icon: LayoutGrid },
] as const;

/**
 * Child-safe nav: 'viewer' is the teen/child family role — Money isn't
 * theirs to see, and Ask Dryvn can act (operational writes). The server
 * enforces both boundaries with 403s; hiding here is courtesy, not the
 * security line.
 */
const VIEWER_HIDDEN_NAV = new Set(["/money", "/dryvn"]);
function useNavItems() {
  const { data: me } = useMe();
  return me?.role === "viewer" ? nav.filter((n) => !VIEWER_HIDDEN_NAV.has(n.to)) : nav;
}

function SchemeToggle() {
  const [scheme, setScheme] = useState<"s1" | "s2">("s1");
  useEffect(() => {
    const stored = (localStorage.getItem("dryvn-scheme") as "s1" | "s2" | null) ?? "s1";
    setScheme(stored);
    document.documentElement.setAttribute("data-scheme", stored);
  }, []);
  const apply = (s: "s1" | "s2") => {
    setScheme(s);
    document.documentElement.setAttribute("data-scheme", s);
    localStorage.setItem("dryvn-scheme", s);
  };
  return (
    <div
      className="inline-flex items-center rounded-full p-0.5 text-[11px] font-medium"
      style={{ backgroundColor: "var(--dryvn-page-tint)" }}
    >
      {[
        { id: "s1" as const, label: "Daylight", Icon: Sun },
        { id: "s2" as const, label: "Midnight", Icon: Moon },
      ].map((s) => {
        const active = scheme === s.id;
        return (
          <button
            key={s.id}
            onClick={() => apply(s.id)}
            className="flex items-center gap-1.5 rounded-full px-2.5 py-1 transition-colors"
            style={{
              backgroundColor: active ? "var(--dryvn-cyan)" : "transparent",
              color: active ? "#FFFFFF" : "var(--dryvn-grey)",
              boxShadow: active ? "var(--shadow-glow)" : "none",
            }}
          >
            <s.Icon className="h-3 w-3" />
            {s.label}
          </button>
        );
      })}
    </div>
  );
}

export function AppShell({ children }: { children: ReactNode }) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const items = useNavItems();
  const isActive = (to: string) =>
    to === "/" ? pathname === "/" : pathname === to || pathname.startsWith(to + "/");

  // Collapsible desktop rail (founder ask 2026-07-27). Collapsed = icon
  // rail; the choice persists per device like the scheme toggle.
  const [collapsed, setCollapsed] = useState(false);
  useEffect(() => {
    setCollapsed(localStorage.getItem("dryvn-rail") === "collapsed");
  }, []);
  const toggleRail = () => {
    setCollapsed((c) => {
      localStorage.setItem("dryvn-rail", c ? "open" : "collapsed");
      return !c;
    });
  };

  return (
    <div className="min-h-screen lg:flex" style={{ backgroundColor: "var(--dryvn-bg)" }}>
      {/* Desktop rail */}
      <aside
        className={`hidden lg:flex lg:flex-col lg:border-r transition-all ${
          collapsed ? "lg:w-[72px]" : "lg:w-64"
        }`}
        style={{ borderColor: "var(--dryvn-line)", backgroundColor: "var(--dryvn-card)" }}
      >
        <div className={collapsed ? "px-3 pt-8 pb-4" : "px-6 pt-8 pb-6"}>
          {!collapsed && (
            <>
              <img src={dryvnLogo.url} alt="Dryvn Life" className="h-14 w-auto object-contain" />
              <p className="mt-3 text-xs" style={{ color: "var(--dryvn-grey)" }}>
                Run your life by text.
              </p>
            </>
          )}
          <button
            type="button"
            onClick={toggleRail}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            className={`grid h-9 w-9 place-items-center rounded-full border transition-colors ${
              collapsed ? "mx-auto" : "mt-4"
            }`}
            style={{ borderColor: "var(--dryvn-line)", color: "var(--dryvn-grey)" }}
          >
            {collapsed ? (
              <PanelLeftOpen className="h-4 w-4" />
            ) : (
              <PanelLeftClose className="h-4 w-4" />
            )}
          </button>
        </div>
        <nav className={`flex-1 pb-6 ${collapsed ? "px-3" : "px-4"}`}>
          {items.map(({ to, label, icon: Icon }) => {
            const active = isActive(to);
            return (
              <Link
                key={to}
                to={to}
                title={label}
                className={`relative flex items-center rounded-full py-2.5 text-sm transition-colors ${
                  collapsed ? "justify-center" : "gap-3 px-4"
                }`}
                style={{
                  color: active ? "var(--dryvn-primary-deep)" : "var(--dryvn-ink)",
                  backgroundColor: active
                    ? "color-mix(in oklab, var(--dryvn-life) 22%, transparent)"
                    : "transparent",
                  fontWeight: active ? 500 : 400,
                }}
              >
                {active && (
                  <span
                    className="absolute left-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-r-full"
                    style={{ backgroundColor: "var(--dryvn-life)" }}
                  />
                )}
                <Icon className="h-4 w-4" />
                {!collapsed && label}
              </Link>
            );
          })}
        </nav>
        {!collapsed && (
          <div className="px-6 pb-6">
            <SchemeToggle />
            <p className="mt-4 text-[11px] leading-relaxed" style={{ color: "var(--dryvn-grey)" }}>
              Your data is yours. Never sold, never judged.
            </p>
          </div>
        )}
      </aside>

      {/* Main */}
      <main className="flex-1 pb-32 lg:pb-16">
        <div className="mx-auto max-w-5xl px-4 pt-4 lg:px-10 lg:pt-10">
          {/* Mobile header with scheme toggle */}
          <div className="mb-4 flex items-center justify-between lg:hidden">
            <img src={dryvnLogo.url} alt="Dryvn Life" className="h-9 w-auto object-contain" />
            <SchemeToggle />
          </div>
          {children}

          {/* Privacy footer */}
          <p
            className="mt-16 text-center text-[11px] italic lg:mt-24"
            style={{ color: "var(--dryvn-grey)" }}
          >
            Your data is yours. Never sold, never judged.
          </p>
        </div>
      </main>

      {/* Mobile bottom bar */}
      <nav
        className="fixed bottom-0 left-0 right-0 z-30 border-t lg:hidden"
        style={{
          borderColor: "var(--dryvn-line)",
          backgroundColor: "color-mix(in oklab, var(--dryvn-card) 92%, transparent)",
          backdropFilter: "blur(10px)",
        }}
      >
        <ul className="grid" style={{ gridTemplateColumns: `repeat(${items.length}, 1fr)` }}>
          {items.map(({ to, label, icon: Icon }) => {
            const active = isActive(to);
            return (
              <li key={to}>
                <Link
                  to={to}
                  className="relative flex flex-col items-center gap-1 py-2.5 text-[11px]"
                  style={{
                    color: active ? "var(--dryvn-primary-deep)" : "var(--dryvn-grey)",
                    fontWeight: active ? 500 : 400,
                  }}
                >
                  {active && (
                    <span
                      className="absolute top-0 h-[3px] w-8 rounded-b-full"
                      style={{ backgroundColor: "var(--dryvn-life)" }}
                    />
                  )}
                  <Icon className="h-5 w-5" />
                  {label}
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>
    </div>
  );
}
